import { cva } from "class-variance-authority";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";

export const worflowBadgeVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded border p-0 min-w-[24px] h-[24px] text-caption text-gray-700 font-title px-2 py-1",
  {
    variants: {
      variant: {
        "striped-gray":
          "bg-[repeating-linear-gradient(-45deg,_rgba(0,0,0,0.05),_rgba(0,0,0,0.05)_4px,_transparent_4px,_transparent_8px)] bg-white text-gray-900/60 border-gray-200",
        gray: "bg-gray-200 text-gray-900 border-gray-500",
        blue: "bg-blue-200 text-blue-900 border-blue-500",
        yellow: "bg-yellow-200 text-orange-900 border-yellow-500",
      },
    },
    defaultVariants: {
      variant: "gray",
    },
  },
);

export type WorkflowBadgeProps = {
  /**
   * The translation key for the label to display
   */
  labelKey: string;
  /**
   * The variant determines the color scheme.
   * Common values: "blue", "green", "yellow", "white"
   * Falls back to "default" (gray) for unknown variants
   */
  variant?: string;
  /**
   * Additional className for customization
   */
  className?: string;
};

function toVariant(
  variant?: string,
): "gray" | "blue" | "yellow" | "striped-gray" {
  switch (variant) {
    case "striped-gray":
      return "striped-gray";
    case "gray":
      return "gray";
    case "blue":
      return "blue";
    case "yellow":
      return "yellow";
    default:
      return "gray";
  }
}

/**
 * WorkflowBadge - A generic badge component for workflow-related displays
 *
 * Handles both workflow steps and workflow conclusions with flexible variant support.
 * If a variant is not recognized, it falls back to a default gray style.
 *
 * @example
 * // Workflow step
 * <WorkflowBadge labelKey="in_progress" variant="yellow" />
 *
 * @example
 * // Workflow conclusion (no variant, uses default)
 * <WorkflowBadge labelKey="fraud_detected" />
 */
export const WorkflowBadge: React.FC<WorkflowBadgeProps> = ({
  labelKey,
  variant,
  className,
}) => {
  const { t } = useTranslation();

  return (
    <div className={cn("flex items-center", className)}>
      <div
        className={cn(
          "px-2 py-1  whitespace-nowrap",
          worflowBadgeVariants({ variant: toVariant(variant) }),
        )}
      >
        {t(labelKey)}
      </div>
    </div>
  );
};
